import React, {
  Fragment,
  useState,
  useContext,
  useEffect,
  useMemo,
} from 'react'
import {
  Button,
  notification,
  DatePicker,
  Tabs,
  Form,
  Select,
  Drawer,
} from 'antd'

import client from 'apollo'
import { AppContext } from 'AppContext'
import CustomMeta from 'components/CustomMeta'
import { CREATE_NEW_TICKET } from './graphql/Mutations'
import CreateTicketModal from './CreateTicketModal'
import { useQuery } from '@apollo/react-hooks'
import { GET_NEW_TICKET, GET_NEW_TICKETS, GET_PRIORITY_SLA_CONFIG } from './graphql/Queries'
import TabNewTickets from './TabTickets/TabNewTickets'
import FilterBackButton from 'assets/images/new-images/filter-back-button.svg'
import {
  ArrowLeftOutlined,
  DownloadOutlined,
  FilterOutlined,
  PlusOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons'

import './index.css'
import history from 'CustomHistory'
import get from 'lodash/get'
import moment from 'moment'
import { GET_USERS_FOR_SELECT } from 'modules/Users/graphql/Queries'
import { GET_DEPARTMENTS } from './Settings/graphql/Queries'
import TicketBulkActionModel from './TabTickets/TicketBulkActionModel'
import { CUSTOM_CSV_DOWNLOAD } from 'modules/Reports/graphql/Queries'
import { canUserAdd, canUserEdit, canUserExport } from 'utils/User'

const { RangePicker } = DatePicker
const { TabPane } = Tabs

const createBadgeStyle = (r, g, b) => ({
  bg: `rgba(${r}, ${g}, ${b}, 0.15)`,
  color: `rgb(${r}, ${g}, ${b})`,
})

const statusBadgeStyles = {
  ALL: createBadgeStyle(0, 32, 96),
  OPEN: createBadgeStyle(0, 105, 45),
  INPROGRESS: createBadgeStyle(150, 15, 105),
  BREACHED: createBadgeStyle(155, 60, 0),
  ESCALATED: createBadgeStyle(150, 0, 0),
  RESOLVED: createBadgeStyle(14, 100, 85),
  CLOSED: createBadgeStyle(30, 85, 15),
  REOPEN: createBadgeStyle(165, 0, 0),
}

export default function () {
  const {
    state: {
      isClient,
      isAdmin,
      isVerificationDone,
      currentUser,
      userPermissions,
      isMobile,
    },
  } = useContext(AppContext)
  const { id, email } = currentUser || {}
  const canAdd = canUserAdd(userPermissions || currentUser, isClient)
  const canEdit = canUserEdit(userPermissions || currentUser, isClient)
  const canExport = canUserExport(userPermissions || currentUser, isClient)
  const [showFormModal, setShowFormModal] = useState(false)
  const [showFormLoading, setShowFormLoading] = useState(false)
  const [showBulkActionModel, setshowBulkActionModel] = useState(false)
  const [sort, setSort] = useState()
  const [skip, setSkip] = useState(0)
  const [limit, setLimit] = useState(10)
  const [filters, setFilters] = useState(
    !isClient && !isAdmin ? { assignUserId: parseInt(id) } : {}
  )

  const [currentPage, setCurrentPage] = useState(1)
  const [activeStatusTab, setActiveStatusTab] = useState('All')
  const [open, setOpen] = useState(false)
  const [form] = Form.useForm()
  const [tickets, setTickets] = useState([])
  const [slaConfigData, setSlaConfigData] = useState([])
  const [userData, setUserData] = useState([])
  const [userLoading, setUserLoading] = useState(true)
  const [userError, setUserError] = useState(null)
  const [departmentData, setDepartmentData] = useState([])
  const [dateRangeDisabled, setDateRangeDisabled] = useState(false)
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [CSVLoading, setCSVLoading] = useState(false)
  const statusKeyMap = {
    // New: 'TRIAGED',
    Open: 'OPEN',
    Resolved: 'RESOLVED',
    Breached: 'BREACHED',
    Escalated: 'ESCALATED',
  }
  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys) => {
      setSelectedRowKeys(newSelectedRowKeys)
    },
    onSelectAll: (selected, selectedRows) => {
      if (selected) {
        setSelectedRowKeys(selectedRows.map((row) => row.key))
      } else {
        setSelectedRowKeys([])
      }
    },
  }

  let variables = { where: filters, limit, skip, sort }
  const { data, loading, error, refetch } = useQuery(GET_NEW_TICKETS, {
    variables,
    fetchPolicy: 'network-only',
  })
  useEffect(() => {
    const handler = () => {
      refetch()
    }

    window.addEventListener('refetchTickets', handler)
    const clearSelectionHandler = () => {
      setSelectedRowKeys([])
    }
    window.addEventListener('clearSelectedRowKeys', clearSelectionHandler)
    return () => window.removeEventListener('refetchTickets', handler)
  }, [refetch])


  const [totalCount, setTotalCount] = useState(0)

  // useEffect(() => {
  //   setSkip(0) // Reset skip to 0 when filters change
  //   setCurrentPage(1) // Reset to first page
  //   // Clear tickets immediately when filters change to avoid showing old data
  //   // setTickets([])
  //   setTotalCount(0)
  //   refetch()
  // }, [filters, refetch])

  useEffect(() => {
    if (data?.newGetTickets) {
      const newTickets = data.newGetTickets.tickets || []
      const newCount = data.newGetTickets.count || 0

      setTickets(newTickets)
      setTotalCount(newCount)
    } else {
      setTickets([])
      setTotalCount(0)
    }
  }, [data])

  const tableData = useMemo(() => {
    const result =
      tickets && tickets.length > 0
        ? tickets.map((ticket, key) => ({
          key: key.toString(),
          ...ticket,
        }))
        : []

    return result
  }, [tickets])

  const getDepartmentName = () => {
    client
      .query({
        query: GET_DEPARTMENTS,
        fetchPolicy: 'network-only',
      })
      .then((res) => {
        setDepartmentData(res?.data?.getDepartments || [])
      })
      .catch((err) => {
        console.error('Error fetching departments:', err)
      })
  }

  function handleCreateTicket(values, resetForm) {
    setShowFormLoading(true)
    let files = []
    if (values?.fileUrl) {
      values.fileUrl.forEach((file) => {
        files.push(file.originFileObj)
      })
      values.fileUrl = files
    } else {
      delete values.fileUrl
    }
    if (isClient && (!values.contactDetail || !String(values.contactDetail).trim())) {
      values.contactDetail = email || ''
    }

    client
      .mutate({
        mutation: CREATE_NEW_TICKET,
        variables: { data: { ...values } },
        refetchQueries: [
          {
            query: GET_NEW_TICKETS,
            variables,
          },
        ],
      })
      .then((res) => {
        notification.success({
          duration: 2.5,
          message: <>Ticket created Successfully</>,
        })
        setShowFormModal(false)
        resetForm()
      })
      .catch((err) => console.log(err))
      .finally(() => setShowFormLoading(false))
  }

  const showDrawer = () => {
    setOpen(true)
  }

  const onClose = () => {
    setOpen(false)
  }
  const handleDateOptionChange = (value) => {
    if (value === 'This Week') {
      const startOfWeek = moment().startOf('week').add(1, 'day')
      const endOfWeek = moment().endOf('week').add(1, 'day')
      form.setFieldsValue({
        dateRange: [startOfWeek, endOfWeek],
      })
      setDateRangeDisabled(true)
    } else if (value === 'Custom') {
      form.setFieldsValue({
        dateRange: [],
      })
      setDateRangeDisabled(false)
    } else {
      let startDate, endDate
      switch (value) {
        case 'Today':
          startDate = moment()
          endDate = moment()
          break
        case 'This Month':
          startDate = moment().startOf('month')
          endDate = moment().endOf('month')
          break
        case 'Last Week':
          startDate = moment().subtract(1, 'week').startOf('week').add(1, 'day')
          endDate = moment().subtract(1, 'week').endOf('week').add(1, 'day')
          break
        case 'Last Month':
          startDate = moment().subtract(1, 'month').startOf('month')
          endDate = moment().subtract(1, 'month').endOf('month')
          break
        default:
          startDate = null
          endDate = null
      }
      form.setFieldsValue({
        dateRange: startDate && endDate ? [startDate, endDate] : [],
      })
      setDateRangeDisabled(true)
    }
  }

  const onFinish = (values) => {
    const dateRange = values.dateRange || []
    let where = {
      assignUserId: values.assignTo || null,
      departmentId: values.department || null,
      priorityId: values.priority || null,
      fromCreatedAtDate: dateRange.length
        ? dateRange[0].format('YYYY-MM-DD')
        : null,
      toCreatedAtDate: dateRange.length
        ? dateRange[1].format('YYYY-MM-DD')
        : null,
      status: values.status || null,
      currentLevel: values.currentLevel,
    }

    where = Object.fromEntries(
      Object.entries(where).map(([key, val]) => {
        if (
          typeof val === 'string' &&
          !isNaN(val) &&
          val.trim() !== '' &&
          Number.isInteger(Number(val))
        ) {
          return [key, Number(val)]
        }
        return [key, val]
      })
    )

    const cleanedWhere = Object.fromEntries(
      Object.entries(where).filter(([_, v]) => v !== null)
    )

    setFilters({
      ...filters,
      ...cleanedWhere,
    })

    setOpen(false)
  }

  const fetchUsers = () => {
    client
      .query({
        query: GET_USERS_FOR_SELECT,
        variables: {
          where: {
            role: ['STAFF', 'SALES_EXECUTIVE', 'ADMIN'],

            isActive: 'true',
          },
          limit: 100000,
        },
      })
      .then((res) => {
        setUserData(res?.data?.getUsers?.users)
        setUserLoading(false)
      })
      .catch((err) => {
        console.log(err)
        setUserLoading(false)
        setUserError(err)
      })
  }
  const fetchSLAConfigStatus = async () => {
    try {
      const res = await client.query({
        query: GET_PRIORITY_SLA_CONFIG,
        fetchPolicy: 'network-only',
      })

      const data = res?.data?.getPrioritySlaConfigs || []
      setSlaConfigData(data)
    } catch (err) {
      console.error('Error fetching SLA config:', err)
    }
  }

  useEffect(() => {
    fetchSLAConfigStatus()
    fetchUsers()
    getDepartmentName()
  }, [])
  const handleCsvDownload = () => {
    setCSVLoading(true)
    const exportTickets = async () => {
      try {
        let tickets = []
        if (selectedRowKeys && selectedRowKeys.length > 0) {
          const results = await Promise.all(
            selectedRowKeys.map((id) =>
              client.query({
                query: GET_NEW_TICKET,
                variables: { id: Number(id) },
                fetchPolicy: 'network-only',
              })
            )
          )
          tickets = results
            .map((r) => r?.data?.newGetTicket)
            .filter(Boolean)
        } else {
          const res = await client.query({
            query: GET_NEW_TICKETS,
            variables: { where: { ...filters }, skip: 0, sort, limit: totalCount },
            fetchPolicy: 'network-only',
          })
          tickets = res.data?.newGetTickets?.tickets || []
        }

        const formatted = tickets.map((ticket) => {
          const allTicketLogUrls = ticket?.ticketLogs
            ?.flatMap((log) => log?.fileUrl || [])
            ?.filter(Boolean) || []

          const allAttachments = [
            ...(ticket?.fileUrl || []),
            ...allTicketLogUrls
          ].filter(Boolean)

          const priorityLabel =
            ticket?.priority?.priorityLevel ||
            (typeof ticket?.priority === 'string' ? ticket.priority : '') ||
            'Priority not decided'

          return {
            ...ticket,
            clientFullName:
              ticket?.generateFor?.firstName && ticket.generateFor?.lastName
                ? `${ticket?.generateFor?.firstName} ${ticket?.generateFor?.lastName}`
                : `${ticket?.generatedBy?.firstName || ''} ${ticket?.generatedBy?.lastName || ''
                  }`.trim(),
            generatedByFullName: `${ticket?.generatedBy?.firstName || ''} ${ticket?.generatedBy?.lastName || ''
              }`.trim(),
            assignUserFullName: `${ticket?.assignUser?.firstName || ''} ${ticket?.assignUser?.lastName || ''
              }`.trim(),
            closedByUserFullName: `${ticket?.closedByUser?.firstName || ''} ${ticket?.closedByUser?.lastName || ''
              }`.trim(),
            clientCodeUserName: ticket?.generateFor?.clientCode
              ? ticket?.generateFor?.clientCode || ticket?.generateFor?.userName
              : ticket?.generatedBy?.clientCode || ticket?.generatedBy?.userName,
            fileUrl: JSON.stringify(allAttachments),
            priorityLabel,
            ticketLogs: ticket?.ticketLogs
              ?.map((item, index) => {
                const formattedDate = moment(item?.createdAt).format(
                  'DD-MM-YYYY hh:mm A'
                )
                const attachment = item?.fileUrl?.length ? item?.fileUrl.join(',') : ''
                return `${index + 1}, ${item?.status}, ${item?.description}, ${item?.statusChanger?.firstName + ' ' + item?.statusChanger?.lastName
                  }, ${formattedDate}, ${attachment}`
              })
              ?.join('\n'),
          }
        })

        const res = await client.query({
          query: CUSTOM_CSV_DOWNLOAD,
          variables: {
            data: {
              data: formatted,
              fields: [
                { value: 'ticketNumber', label: 'Ticket Number' },
                { value: 'clientFullName', label: 'Client Name' },
                { value: 'clientCodeUserName', label: 'Client Code/User Name' },
                { value: 'status', label: 'Status' },
                { value: 'department.departmentName', label: 'Department' },
                { value: 'priorityLabel', label: 'Priority' },
                // { value: 'categoryName', label: 'Category' },
                // { value: 'subCategoryName', label: 'Sub-Category' },
                { value: 'description', label: 'Description' },
                { value: 'ticketLogs', label: 'Status Summary' },
                { value: 'assignUserFullName', label: 'Assigned User' },
                { value: 'generatedByFullName', label: 'Created By' },
                { value: 'closedByUserFullName', label: 'Closed By' },
                { value: 'createdAt', label: 'Created At' },
                { value: 'closedDate', label: 'Closed At' },
                { value: 'updatedAt', label: 'Last Status Timestamp' },
                { value: 'fileUrl', label: 'Attachment' },
              ],
              fileName: 'Tickets',
            },
          },
        })

        if (get(res, 'data.customCSVDownload')) {
          window.open(get(res, 'data.customCSVDownload'), '_blank')
        }
      } catch (err) {
        console.log(err)
      } finally {
        setLimit(10)
        setSkip(0)
        setCSVLoading(false)
      }
    }

    exportTickets()
  }
  const handleStatusTabChange = (key) => {
    setActiveStatusTab(key)
    setCurrentPage(1)

    setFilters((prevFilters) => {
      const updatedFilters = { ...prevFilters }

      if (key === 'All') {
        delete updatedFilters.status
      } else {
        const backendStatus = statusKeyMap[key] || key
        updatedFilters.status = backendStatus
      }

      setSkip(0)

      return updatedFilters
    })
  }

  // When filters are applied (including status filters), compute counts from returned tickets
  const computedCounts = React.useMemo(() => {
    const tickets = data?.newGetTickets?.tickets || []
    const map = {
      total: tickets.length || 0,
      OPEN: 0,
      INPROGRESS: 0,
      BREACHED: 0,
      ESCALATED: 0,
      RESOLVED: 0,
      CLOSED: 0,
      REOPEN: 0,
      NEW: 0,
    }
    tickets.forEach((t) => {
      const s = (t.status || 'OPEN').toUpperCase()
      if (map[s] !== undefined) map[s] = (map[s] || 0) + 1
    })
    return map
  }, [data])
  // (moved) When filters are applied (including status filters), compute counts from returned tickets

  // normalize status filter into an array of uppercase status keys
  // eslint-disable-next-line no-unused-vars
  const selectedStatusArray = useMemo(() => {
    const statusFilter = filters?.status
    if (!statusFilter) return []
    if (Array.isArray(statusFilter)) return statusFilter.map((s) => String(s).toUpperCase())
    return [String(statusFilter).toUpperCase()]
  }, [filters])

  const statusTabs = [
    {
      key: 'All',
      label: 'All',
      // 'All' should always show the total (server-provided or computed sum)
      count: (() => {
        const serverTotal = data?.newGetTickets?.counters?.totalTicket ?? data?.newGetTickets?.count
        if (typeof serverTotal === 'number') return serverTotal
        // fallback: sum computedCounts for all known statuses
        const sum =
          (computedCounts.OPEN || 0) +
          (computedCounts.INPROGRESS || 0) +
          (computedCounts.BREACHED || 0) +
          (computedCounts.ESCALATED || 0) +
          (computedCounts.RESOLVED || 0) +
          (computedCounts.CLOSED || 0) +
          (computedCounts.REOPEN || 0) +
          (computedCounts.NEW || 0)
        return sum || 0
      })(),
    },
    // {
    //   key: 'TRIAGED',
    //   label: 'New (Triaged)',
    //   count: data?.newGetTickets?.counters?.triagedTicket,
    // },
    {
      key: 'OPEN',
      label: 'Open',
      count: (() => {
        const serverVal = data?.newGetTickets?.counters?.openTicket
        const computedVal = computedCounts.OPEN || 0
        return (serverVal ?? computedVal) || 0
      })(),
    },
    {
      key: 'INPROGRESS',
      label: 'In Progress',
      count: (() => {
        const serverVal = data?.newGetTickets?.counters?.inProcessTicket
        const computedVal = computedCounts.INPROGRESS || 0
        return (serverVal ?? computedVal) || 0
      })(),
    },
    {
      key: 'BREACHED',
      label: 'Breached',
      count: (() => {
        const serverVal = data?.newGetTickets?.counters?.breachedTicket
        const computedVal = computedCounts.BREACHED || 0
        return (serverVal ?? computedVal) || 0
      })(),
    },
    {
      key: 'ESCALATED',
      label: 'Escalated',
      count: (() => {
        const serverVal = data?.newGetTickets?.counters?.escalatedTicket
        const computedVal = computedCounts.ESCALATED || 0
        return (serverVal ?? computedVal) || 0
      })(),
    },
    {
      key: 'RESOLVED',
      label: 'Resolved',
      count: (() => {
        const serverVal = data?.newGetTickets?.counters?.resolvedTicket
        const computedVal = computedCounts.RESOLVED || 0
        return (serverVal ?? computedVal) || 0
      })(),
    },
    {
      key: 'REOPEN',
      label: 'Reopened',
      count: (() => {
        const serverVal = data?.newGetTickets?.counters?.reOpenTicket
        const computedVal = computedCounts.REOPEN || 0
        return (serverVal ?? computedVal) || 0
      })(),
    },
  ]

  return (
    <Fragment>
      <CustomMeta title='Tickets' />
      <div className='main_dashboard_div' style={{ padding: '20px 40px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px',
            width: '100%',
          }}
        >
          <div
            role='heading'
            aria-level={1}
            style={{
              fontSize: isMobile ? '18px' : '24px',
              fontWeight: '600',
              lineHeight: '26px',
              letterSpacing: '-0.02em',
              textAlign: 'left',
              color: '#181C32',
              display: 'flex',
              gap: '20px',
            }}
          >
            {isClient && !isVerificationDone ? (
              <div
                className='back_icon_for_advisory'
                onClick={() => history.goBack()}
              >
                <ArrowLeftOutlined />
              </div>
            ) : (
              ''
            )}
            Tickets
          </div>

          <Drawer
            title={null}
            headerStyle={{ display: 'none' }}
            closable={false}
            placement='right'
            onClose={onClose}
            visible={open}
            width={472}
          >
            <div
              style={{
                margin: '32px 0 50px 0',
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
              }}
            >
              <div onClick={onClose}>
                <img src={FilterBackButton} alt='FilterBackButton' />
              </div>
              <div
                style={{
                  fontSize: '24px',
                  fontWeight: '700',
                  lineHeight: '28px',
                  textAlign: 'left',
                  color: '#000000',
                  marginLeft: '16px',
                }}
                onClick={showDrawer}
              >
                Filter
              </div>
            </div>
            <Form
              form={form}
              onFinish={onFinish}
              name='advanced_search'
              style={{ marginTop: '16px' }}
              layout='vertical'
              className='client-detail-form'
            >
              <div
                style={{
                  height: isMobile
                    ? 'calc(100vh - 15rem)'
                    : 'calc(100vh - 12rem)',
                  overflowY: 'auto',
                }}
              >
                <Form.Item name='Date' label='Date'>
                  <Select
                    allowClear
                    placeholder='Select'
                    className='display-field'
                    onChange={handleDateOptionChange}
                    getPopupContainer={(trigger) => trigger.parentElement}
                  >
                    <Select.Option value='Today'>Today</Select.Option>
                    <Select.Option value='This Week'>This Week</Select.Option>
                    <Select.Option value='This Month'>This Month</Select.Option>
                    <Select.Option value='Last Week'>Last Week</Select.Option>
                    <Select.Option value='Last Month'>Last Month</Select.Option>
                    <Select.Option value='Custom'>Custom</Select.Option>
                  </Select>
                </Form.Item>
                <Form.Item name='dateRange' label='Date Range'>
                  <RangePicker
                    style={{
                      width: '100%',
                      height: '40px',
                      borderRadius: '8px',
                      padding: '6px 12px',
                      fontSize: '14px',
                      border: '1px solid #E5E7EB',
                      color: '#111827',
                      backgroundColor: '#fff',
                    }}
                    format='DD-MMM-YY'
                    separator=' to '
                    placeholder={['Start date', 'End date']}
                    className='display-field custom-date-range'
                    getPopupContainer={(trigger) => trigger.parentElement}
                    allowClear
                    disabled={dateRangeDisabled}
                  />
                </Form.Item>
                <Form.Item name='priority' label='Priority'>
                  <Select
                    allowClear
                    placeholder='Select'
                    className='display-field'
                    getPopupContainer={(trigger) => trigger.parentElement}
                  >
                    {slaConfigData?.map((item) => (
                      <Select.Option key={item.id} value={item.id}>
                        {item.priorityLevel}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>

                {/* Department */}
                <Form.Item name='department' label='Department'>
                  <Select
                    allowClear
                    showSearch
                    placeholder='Select'
                    className='display-field'
                    getPopupContainer={(trigger) => trigger.parentElement}
                    optionFilterProp='children'
                  >
                    {departmentData.length === 0 ? (
                      <Select.Option disabled>
                        No departments found
                      </Select.Option>
                    ) : (
                      departmentData.map((dept) => (
                        <Select.Option key={dept.id} value={dept.id}>
                          {dept.departmentName}
                        </Select.Option>
                      ))
                    )}
                  </Select>
                </Form.Item>

                {/* Status */}
                <Form.Item name='status' label='Status'>
                  <Select
                    mode='multiple'
                    allowClear
                    showSearch
                    placeholder='Select'
                    className='display-field'
                    getPopupContainer={(trigger) => trigger.parentElement}
                    maxTagCount={3}
                    maxTagPlaceholder={(omittedValues) => `+${omittedValues.length} more`}
                  >
                    <Select.Option value='OPEN'>OPEN</Select.Option>
                    <Select.Option value='BREACHED'>BREACHED</Select.Option>
                    <Select.Option value='ASSIGNED'>ASSIGNED</Select.Option>
                    {/* <Select.Option value='TRIAGED'>TRIAGED</Select.Option> */}
                    <Select.Option value='ESCALATED'>ESCALATED</Select.Option>
                    <Select.Option value='INPROGRESS'>INPROGRESS</Select.Option>
                    <Select.Option value='REVIEW'>REVIEW</Select.Option>
                    <Select.Option value='RESOLVED'>RESOLVED</Select.Option>
                    <Select.Option value='CLOSED'>CLOSED</Select.Option>
                    <Select.Option value='REOPEN'>REOPEN</Select.Option>
                  </Select>
                </Form.Item>

                {/* Assign To */}
                <Form.Item name='assignTo' label='Assign To'>
                  <Select
                    allowClear
                    showSearch
                    loading={userLoading}
                    placeholder={userLoading ? 'Loading users...' : 'Select'}
                    className='display-field'
                    getPopupContainer={(trigger) => trigger.parentElement}
                    notFoundContent={userError ? 'Failed to load users' : null}
                    optionFilterProp='label' // search against label
                    optionLabelProp='label' // 👈 use label for display after select
                    filterOption={(input, option) =>
                      option?.label
                        ?.toString()
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  >
                    {userData?.map((user) => (
                      <Select.Option
                        key={user.id}
                        value={user.id}
                        label={`${user.firstName} ${user.lastName} (${user.role || 'INTERNAL TEAM'
                          })`}
                      >
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                          }}
                        >
                          <span>{`${user.firstName} ${user.lastName}`}</span>
                          <span style={{ color: '#999', fontStyle: 'italic' }}>
                            ({user.role || 'INTERNAL TEAM'})
                          </span>
                        </div>
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>

                {/* SLA Risk Level */}
                <Form.Item name='currentLevel' label='Current Level'>
                  <Select
                    allowClear
                    placeholder='Select'
                    className='display-field'
                    getPopupContainer={(trigger) => trigger.parentElement}
                  >
                    <Select.Option value={null}>Team Member</Select.Option>
                    <Select.Option value={'TEAM_LEADER'}>
                      Team Leader
                    </Select.Option>
                    <Select.Option value={'DEPARTMENT_HEAD'}>
                      Department Head
                    </Select.Option>
                    <Select.Option value={'CRM_ADMIN'}>CRM Admin</Select.Option>
                  </Select>
                </Form.Item>
              </div>

              <div style={{ display: 'flex', gap: '20px', height: '56px' }}>
                <Button
                  onClick={() => {
                    form.resetFields()
                    setFilters({})
                    setOpen(false)
                    setDateRangeDisabled(false)
                  }}
                  style={{
                    width: '100%',
                    borderRadius: '8px',
                    padding: '16px',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  Reset
                </Button>
                <Button
                  loading={loading}
                  type='primary'
                  htmlType='submit'
                  style={{
                    width: '100%',
                    borderRadius: '8px',
                    padding: '16px',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  Search
                </Button>
              </div>
            </Form>
          </Drawer>

          <div style={{ display: 'flex', gap: '10px' }}>
            {isAdmin ? (
              <>
                {canExport && (
                  <Button
                    onClick={() => handleCsvDownload()}
                    loading={CSVLoading}
                    style={{
                      margin: '0',
                      padding: '8px 16px',
                      fontSize: '14px',
                      fontWeight: '600',
                      lineHeight: '24px',
                      textAlign: 'left',
                      color: '#AF0000',
                      background: '#AF00000D',
                      border: '1px solid #AF000080',
                      height: '40px',
                      borderRadius: '8px',
                    }}
                  >
                    {CSVLoading ? 'Exporting...' : 'Export'}
                    <DownloadOutlined />
                  </Button>
                )}
                {canEdit && (
                  <Button
                    onClick={() => setshowBulkActionModel(true)}
                    disabled={selectedRowKeys.length === 0}
                    style={{
                      margin: '0',
                      padding: '8px 16px',
                      fontSize: '14px',
                      fontWeight: '600',
                      lineHeight: '24px',
                      textAlign: 'left',
                      color: '#AF0000',
                      background: '#AF00000D',
                      border: '1px solid #AF000080',
                      height: '40px',
                      borderRadius: '8px',
                    }}
                  >
                    Bulk Actions <UnorderedListOutlined />
                  </Button>
                )}
                <Button
                  onClick={() => showDrawer()}
                  style={{
                    margin: '0',
                    padding: '8px 16px',
                    fontSize: '14px',
                    fontWeight: '600',
                    lineHeight: '24px',
                    textAlign: 'left',
                    color: '#AF0000',
                    background: '#AF00000D',
                    border: '1px solid #AF000080',
                    height: '40px',
                    borderRadius: '8px',
                  }}
                >
                  Filter <FilterOutlined />
                </Button>
                {canAdd && (
                  <Button
                    style={{
                      margin: '0',
                      padding: '8px 16px',
                      fontSize: '14px',
                      fontWeight: '600',
                      lineHeight: '24px',
                      textAlign: 'left',
                      color: '#FFFFFF',
                      background: '#AF0000',
                      border: '1px solid #AF0000',
                      height: '40px',
                      borderRadius: '8px',
                    }}
                    onClick={() => setShowFormModal(true)}
                  >
                    Generate Ticket <PlusOutlined />
                  </Button>
                )}
              </>
            ) : (
              <>
                <Button
                  onClick={() => showDrawer()}
                  style={{
                    margin: '0',
                    padding: '8px 16px',
                    fontSize: '14px',
                    fontWeight: '600',
                    lineHeight: '24px',
                    textAlign: 'left',
                    color: '#AF0000',
                    background: '#AF00000D',
                    border: '1px solid #AF000080',
                    height: '40px',
                    borderRadius: '8px',
                  }}
                >
                  Filter <FilterOutlined />
                </Button>
                {canAdd && (
                  <Button
                    style={{
                      margin: '0',
                      padding: '8px 16px',
                      fontSize: '14px',
                      fontWeight: '600',
                      lineHeight: '24px',
                      textAlign: 'left',
                      color: '#FFFFFF',
                      background: '#AF0000',
                      border: '1px solid #AF0000',
                      height: '40px',
                      borderRadius: '8px',
                    }}
                    onClick={() => setShowFormModal(true)}
                  >
                    Generate Ticket{' '}
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
        <Tabs
          activeKey={activeStatusTab}
          onChange={handleStatusTabChange}
          style={{ marginBottom: 16 }}
          className='status-filter-tabs'
        >
          {statusTabs.map((tab) => {
            const badgeKey = tab.key?.toString().toUpperCase() || ''
            const badgeStyle = statusBadgeStyles[badgeKey] || {
              bg: '#7E829929',
              color: '#1F2933',
            }

            return (
              <TabPane
                key={tab.key}
                tab={
                  <span
                    style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                  >
                    <span
                      style={{
                        fontWeight: activeStatusTab === tab.key ? '600' : '400',
                        color: badgeStyle.color,
                      }}
                    >
                      {tab.label}
                    </span>
                    {typeof tab.count === 'number' && (
                      <span
                        style={{
                          backgroundColor: badgeStyle.bg,
                          color: badgeStyle.color,
                          borderRadius: '12px',
                          padding: '2px 10px',
                          fontSize: '16px',
                          fontWeight: 600,
                          lineHeight: 1,
                        }}
                      >
                        {tab.count}
                      </span>
                    )}
                  </span>
                }
              />
            )
          })}
        </Tabs>

        {showFormModal && (
          <CreateTicketModal
            visible={showFormModal}
            loading={showFormLoading}
            onCreate={handleCreateTicket}
            onCancel={() => setShowFormModal(false)}
            fromFaq={false}
          />
        )}
        {showBulkActionModel && (
          <TicketBulkActionModel
            visible={showBulkActionModel}
            onCancel={() => setshowBulkActionModel(false)}
            selectedTickets={selectedRowKeys}
            usersData={userData}
          />
        )}

        {statusTabs.length > 0 ? (
          <>
            <TabNewTickets
              key={`tickets-${JSON.stringify(filters)}-${skip}-${limit}`}
              setSort={setSort}
              setSkip={setSkip}
              skip={skip}
              setLimit={setLimit}
              setFilters={setFilters}
              limit={limit}
              filters={filters}
              data={data}
              loading={loading}
              error={error}
              tableData={tableData}
              totalCount={totalCount}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              rowSelection={rowSelection}
              departmentData={departmentData}
              expandHeaderTitle={'Action'}
            />
          </>
        ) : (
          ''
        )}
      </div>
    </Fragment>
  )
}